import { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { DragDropContext } from 'react-beautiful-dnd';
import {
  IlogisticServiceTrelloCardData,
  ITrello,
  LOGISTIC_DROPPABLE_ID,
  LOGISTIC_SERVICE_STATUS_NAME,
  TlogisticsMovementsTypes
} from '../../../../types/services';
import LogisticsServicesTrelloModals from '../ModalsLogisticsService/LogisticsServicesTrelloModals';
import { displayErrorNotification } from '../../../../utils/displayNudosStandardNotifications';
import LogisticServiceSpaceCard from '../LogisticServiceSpaceCard/LogisticServiceSpaceCard';
import LogisticServiceTrelloColumn from '../LogisticServiceTrelloColumn/LogisticServiceTrelloColumn';
import useLsTrelloState from '../../../../state/useLsTrelloState';
import useOnlyDevelopers from '../../../../state/setOnlyDevelopers';
import {
  displayConfigurationTransitionBlockedNotification,
  evaluateLogisticServiceActiveTransition,
  GO_TO_CONFIGURATIONS_BUTTON_TEXT,
  LOGISTIC_SERVICE_ACTIVE_BLOCK_MESSAGE
} from '../../../../utils/productConfigurationSummary.utils';
import './LogisticsServicesTrello.scss';

const LogisticsServicesTrello = ({
  searchText,
  changeStateFirsLoad
}: {
  searchText: string;
  changeStateFirsLoad: (state: boolean) => void;
}) => {
  const { push } = useHistory();
  const { logisticServices, setLogisticServices, initialLogisticServices, setDragEndData } = useLsTrelloState();
  const { isOnlyDevsLogistics } = useOnlyDevelopers();

  const [showCancelModal, setShowCancelModal] = useState<boolean>(false);
  const [showConfirmModal, setShowConfirmModal] = useState<boolean>(false);
  const [showDelayedModal, setShowDelayedModal] = useState<boolean>(false);
  const [showActiveModal, setShowActiveModal] = useState<boolean>(false);
  const [showDeliveredModal, setShowDeliveredModal] = useState<boolean>(false);
  const [showInProcessModal, setShowInProcessModal] = useState<boolean>(false);
  const [selectedLogisticServiceDataForModal, setSelectedLogisticServiceDataForModal] =
    useState<IlogisticServiceTrelloCardData>();

  const { CANCELLED, CONFIRMED, ACTIVE, DELIVERED, INPROCESS, DELAYED, TOCONFIRMED } = LOGISTIC_SERVICE_STATUS_NAME;
  const { CANCELLED_ID, CONFIRMED_ID, ACTIVE_ID, DELIVERED_ID, INPROCESS_ID, DELAYED_ID, TOCONFIRMED_ID } =
    LOGISTIC_DROPPABLE_ID;

  const modifyLocalLists = (
    item: IlogisticServiceTrelloCardData,
    origin: TlogisticsMovementsTypes,
    destination: TlogisticsMovementsTypes
  ) => {
    const originList = logisticServices?.[origin]?.filter(originElement => originElement.id !== item.id);
    const oldDestinationList = logisticServices?.[destination] || [];
    const newDestinationList = [item, ...oldDestinationList];
    setLogisticServices({ ...logisticServices, [origin]: originList, [destination]: newDestinationList });
  };

  const handleChangeCards = async (result: ITrello) => {
    const draggableId = Number(result?.draggableId);
    const pendingItem = logisticServices?.pendings?.find(pendingElement => pendingElement.id === draggableId);
    const confirmItem = logisticServices?.confirmed?.find(confirmElement => confirmElement.id === draggableId);
    const inProcessItem = logisticServices?.inProcess?.find(inProcessElement => inProcessElement.id === draggableId);
    const activeItem = logisticServices?.actives?.find(activeElement => activeElement.id === draggableId);
    const delayedItem = logisticServices?.delayed?.find(delayedElement => delayedElement.id === draggableId);
    const originIsPending = result.source?.droppableId === TOCONFIRMED_ID;
    const originIsConfirmed = result?.source?.droppableId === CONFIRMED_ID;
    const originIsInProcess = result?.source?.droppableId === INPROCESS_ID;
    const originIsActive = result.source?.droppableId === ACTIVE_ID;
    const originIsDelayed = result?.source?.droppableId === DELAYED_ID;
    const destinationIsConfirm = result.destination?.droppableId === CONFIRMED_ID;
    const destinationIsInProcess = result.destination?.droppableId === INPROCESS_ID;
    const destinationIsActive = result.destination?.droppableId === ACTIVE_ID;
    const destinationIsCanceled = result.destination?.droppableId === CANCELLED_ID;
    const destinationIsDelivered = result.destination?.droppableId === DELIVERED_ID;
    const notDelivered = originIsPending || originIsConfirmed || originIsActive || originIsDelayed || originIsInProcess;
    const originViewedChanges = pendingItem?.addressIssue?.origin?.viewedChanges;
    const destinationViewedChanges = pendingItem?.addressIssue?.destination?.viewedChanges;
    const originAddressEditedByUser = pendingItem?.addressIssue?.origin?.editedBy === 'user';
    const destinationAddressEditedByUser = pendingItem?.addressIssue?.destination?.editedBy === 'user';
    const originAddressEditedByUserAndNotViewed = originAddressEditedByUser && !originViewedChanges;
    const destinationAddressEditedByUserAndNotViewed = destinationAddressEditedByUser && !destinationViewedChanges;
    if (notDelivered && destinationIsCanceled && !!draggableId) {
      setSelectedLogisticServiceDataForModal(pendingItem || confirmItem || activeItem || delayedItem || inProcessItem);
      if (pendingItem) {
        // pendiente a cancelado
        modifyLocalLists(pendingItem, 'pendings', 'cancelled');
      }
      if (confirmItem) {
        //confirmado a cancelado
        modifyLocalLists(confirmItem, 'confirmed', 'cancelled');
      }
      if (inProcessItem) {
        //procesado a cancelado
        modifyLocalLists(inProcessItem, 'inProcess', 'cancelled');
      }
      if (activeItem) {
        //activo a cancelado
        modifyLocalLists(activeItem, 'actives', 'cancelled');
      }
      if (delayedItem) {
        //atrasado a cancelado
        modifyLocalLists(delayedItem, 'delayed', 'cancelled');
      }
      setShowCancelModal(true);
    }
    if (originIsPending && destinationIsConfirm && pendingItem && !!draggableId) {
      //pendiente a confirmado
      if (originAddressEditedByUserAndNotViewed || destinationAddressEditedByUserAndNotViewed)
        return displayErrorNotification({
          customJSXMessage: <>No puedes mover el servicio hasta revisar los cambios realizados por el usuario</>
        });
      if (!pendingItem?.quoteDocumentUrl)
        return displayErrorNotification({
          customJSXMessage: <>Se necesita una cotización para mover el servicio logístico a confirmado</>
        });
      modifyLocalLists(pendingItem, 'pendings', 'confirmed');
      setSelectedLogisticServiceDataForModal(pendingItem);
      setShowConfirmModal(true);
    }
    if (originIsConfirmed && destinationIsInProcess && !!draggableId && confirmItem) {
      // confirmado a proceso
      const originViewedChanges = confirmItem?.addressIssue?.origin?.viewedChanges;
      const destinationViewedChanges = confirmItem?.addressIssue?.destination?.viewedChanges;
      const originAddressEdited = confirmItem?.addressIssue?.origin?.editedBy === 'user';
      const destinationAddressEdited = confirmItem?.addressIssue?.destination?.editedBy === 'user';
      if ((!originViewedChanges && !!originAddressEdited) || (!destinationViewedChanges && !!destinationAddressEdited))
        return displayErrorNotification({
          customJSXMessage: <>No puedes mover el servicio hasta revisar los cambios realizados por el usuario</>
        });
      modifyLocalLists(confirmItem, 'confirmed', 'inProcess');
      setSelectedLogisticServiceDataForModal(confirmItem);
      setShowInProcessModal(true);
    }
    if (originIsInProcess && destinationIsActive && !!draggableId && inProcessItem?.id) {
      if (isOnlyDevsLogistics) {
        try {
          const { isBlocked } = await evaluateLogisticServiceActiveTransition(inProcessItem.id);

          if (isBlocked) {
            return displayConfigurationTransitionBlockedNotification({
              displayErrorNotification,
              mainMessage: LOGISTIC_SERVICE_ACTIVE_BLOCK_MESSAGE,
              button1Text: GO_TO_CONFIGURATIONS_BUTTON_TEXT,
              button1Action: () => push(`/logistic-service/details/${inProcessItem.id}`, { initialTab: 2 })
            });
          }
        } catch {
          displayErrorNotification();
          return;
        }
      }

      modifyLocalLists(inProcessItem, 'inProcess', 'actives');
      setSelectedLogisticServiceDataForModal(inProcessItem);
      setShowActiveModal(true);
    }
    if ((originIsActive || originIsDelayed) && destinationIsDelivered) {
      setSelectedLogisticServiceDataForModal(activeItem || delayedItem);
      if (activeItem) {
        modifyLocalLists(activeItem, 'actives', 'delivered');
      }
      if (delayedItem) {
        modifyLocalLists(delayedItem, 'delayed', 'delivered');
      }
      setShowDeliveredModal(true);
    }
  };

  const initialData = () => {
    if (initialLogisticServices) setLogisticServices(initialLogisticServices);
  };

  const closeAndRestoreInitialList = () => {
    setShowActiveModal(false);
    setShowDelayedModal(false);
    setShowDeliveredModal(false);
    setShowConfirmModal(false);
    setShowCancelModal(false);
    setShowInProcessModal(false);
    setSelectedLogisticServiceDataForModal(undefined);
    initialData();
  };

  const openDelayedModalCallback = (logisticServiceTrelloData: IlogisticServiceTrelloCardData) => {
    setSelectedLogisticServiceDataForModal(logisticServiceTrelloData);
    setShowDelayedModal(true);
  };

  return (
    <>
      <LogisticsServicesTrelloModals
        selectedLogisticServiceDataForModal={selectedLogisticServiceDataForModal}
        setSelectedLogisticServiceDataForModal={setSelectedLogisticServiceDataForModal}
        closeAndRestoreList={closeAndRestoreInitialList}
        cancelModalData={{ showModal: showCancelModal, closeModal: () => setShowCancelModal(false) }}
        confirmModalData={{ showModal: showConfirmModal, closeModal: () => setShowConfirmModal(false) }}
        processModalData={{ showModal: showInProcessModal, closeModal: () => setShowInProcessModal(false) }}
        delayedModalData={{ showModal: showDelayedModal, closeModal: () => setShowDelayedModal(false) }}
        deliveredModalData={{ showModal: showDeliveredModal, closeModal: () => setShowDeliveredModal(false) }}
        activeModalData={{ showModal: showActiveModal, closeModal: () => setShowActiveModal(false) }}
      />
      <DragDropContext
        onDragEnd={result => {
          setDragEndData(result);
          void handleChangeCards(result);
        }}
      >
        <div id="logisticsServicesTrello">
          <div className="logisticsGeneralContainer">
            <div className="activeLogisticsServices">
              <LogisticServiceTrelloColumn
                status={TOCONFIRMED}
                searchText={searchText}
                changeStateFirsLoad={changeStateFirsLoad}
              />
              <LogisticServiceSpaceCard spaceName="Manual" />
              <LogisticServiceTrelloColumn
                status={CONFIRMED}
                searchText={searchText}
                changeStateFirsLoad={changeStateFirsLoad}
              />
              <LogisticServiceSpaceCard spaceName="Manual" />
              <LogisticServiceTrelloColumn
                status={INPROCESS}
                searchText={searchText}
                changeStateFirsLoad={changeStateFirsLoad}
              />
              <LogisticServiceSpaceCard spaceName="Manual" />
              <LogisticServiceTrelloColumn
                status={ACTIVE}
                searchText={searchText}
                changeStateFirsLoad={changeStateFirsLoad}
              />
              <LogisticServiceSpaceCard spaceName="Automatico" />
              <LogisticServiceTrelloColumn
                status={DELAYED}
                searchText={searchText}
                changeStateFirsLoad={changeStateFirsLoad}
                openDelayedModalCallback={openDelayedModalCallback}
              />
              <LogisticServiceSpaceCard spaceName="Manual" />
              <LogisticServiceTrelloColumn
                status={DELIVERED}
                searchText={searchText}
                changeStateFirsLoad={changeStateFirsLoad}
              />
            </div>
            <LogisticServiceTrelloColumn
              status={CANCELLED}
              searchText={searchText}
              changeStateFirsLoad={changeStateFirsLoad}
            />
          </div>
        </div>
      </DragDropContext>
    </>
  );
};
export default LogisticsServicesTrello;
